import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Flag, Plus, Edit3, Trash2, Check, AlertTriangle, Save, X,
  CalendarDays, Star, DollarSign, Sparkles,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button, Card, Field, Input, Pill, Select, Spinner, Textarea } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// MilestonesTab — T-202
//
// Client-reportable, structured milestones per project. Distinct from:
//   • migration 053's project_milestones (free-text ad-hoc, separate concept)
//   • project_bookings (internal scheduling)
//
// Features:
//   • List milestones grouped by status (Active / Done / Deferred)
//   • Apply Template — seeds milestones from milestone_templates via RPC
//   • Add manual ad-hoc milestone (template_line_id = null, won't be wiped on re-seed)
//   • Edit / delete / mark-done
//   • Star indicator for top-level (★), payment trigger badge ($)
//   • Status pills, scope owner display, NBN MS-codes when present
//
// Smart-shift cascade with confirmation prompt — Chunk C, separate ticket.
// PDF + Excel export — Chunk E, separate ticket.
// =============================================================================

type MilestoneStatus = "planned" | "in_progress" | "done" | "deferred" | "blocked";

interface ProjectMilestone {
  milestone_id: string;
  project_id: string;
  template_line_id: string | null;
  milestone_code: string | null;
  name: string;
  scope_owner: string | null;
  dependency_anchor: string;
  offset_days: number | null;
  planned_date: string | null;
  actual_date: string | null;
  status: MilestoneStatus;
  is_starred: boolean;
  is_payment_trigger: boolean;
  payment_pct: number | null;     // T-202 — % of contract triggered by this milestone
  client_visible: boolean;
  sort_order: number;
  comment: string | null;
  internal_notes: string | null;
}

interface MilestoneTemplate {
  template_id: string;
  template_name: string;
  description: string | null;
  is_default: boolean;
  client_id: string | null;
}

interface Props {
  project: any;
  canManage: boolean;
}

const STATUS_TONES: Record<MilestoneStatus, any> = {
  planned:     "default",
  in_progress: "won",
  done:        "ok",
  deferred:    "warning",
  blocked:     "lost",
};

const STATUS_LABELS: Record<MilestoneStatus, string> = {
  planned:     "Planned",
  in_progress: "In progress",
  done:        "Done",
  deferred:    "Deferred",
  blocked:     "Blocked",
};

// =============================================================================
// Forecast-date computation (T-202 UX improvement)
//
// When a milestone has no committed planned_date but its dependency_anchor can
// be resolved (chain hasn't broken at a standalone), derive what the date
// WOULD be from the current chain. Displayed in italic + "fcst" tag so the
// manager can distinguish forecast from committed dates.
//
// Mirrors the server-side logic in app.add_working_days() / the seed RPC:
// Mon-Sat working days, Sundays skipped.
// =============================================================================
function addWorkingDaysIso(dateStr: string, days: number): string {
  const date = new Date(dateStr + "T00:00:00");
  let remaining = days;
  while (remaining > 0) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() !== 0) {  // 0 = Sunday in JS
      remaining--;
    }
  }
  return date.toISOString().slice(0, 10);
}

function computeForecastDate(
  m: ProjectMilestone,
  all: ProjectMilestone[],
  visited: Set<string> = new Set(),
): string | null {
  // If a date is already committed/seeded on the row, that IS the forecast.
  if (m.planned_date) return m.planned_date;

  // Cycle guard
  if (visited.has(m.milestone_id)) return null;
  visited.add(m.milestone_id);

  if (m.dependency_anchor === "standalone") return null;

  if (m.dependency_anchor === "previous") {
    // Walk back to the most recent prior milestone by sort_order
    const prev = all
      .filter(x => x.sort_order < m.sort_order)
      .sort((a, b) => b.sort_order - a.sort_order)[0];
    if (!prev) return null;
    const anchorDate = computeForecastDate(prev, all, visited);
    if (!anchorDate) return null;
    return addWorkingDaysIso(anchorDate, m.offset_days ?? 0);
  }

  // Comma-separated milestone_code list (e.g. 'MS7000, MS7015')
  const codes = m.dependency_anchor.split(",").map(c => c.trim()).filter(Boolean);
  const anchorDates: string[] = [];
  for (const code of codes) {
    const anchor = all.find(x => x.milestone_code === code);
    if (!anchor) continue;
    const d = computeForecastDate(anchor, all, visited);
    if (d) anchorDates.push(d);
  }
  if (anchorDates.length === 0) return null;
  const maxDate = anchorDates.sort()[anchorDates.length - 1];
  return addWorkingDaysIso(maxDate, m.offset_days ?? 0);
}

// =============================================================================
// Shift cascade computation — Chunk C
//
// When a manager edits a milestone's planned_date, walk the dependency chain
// forward and compute what every downstream milestone's date "should" become.
// We propose the shift via a confirmation modal — the manager can review each
// row and uncheck any they don't want shifted (e.g. milestones with manual
// committed dates that don't follow the formula).
//
// Algorithm:
//   • Seed an override map with {edited_id → new_date}.
//   • Iterate over rows in sort_order; for each non-standalone, non-actual row,
//     compute its anchor date using the override map (else stored planned_date)
//     and check whether the formula's output differs from its current stored
//     planned_date.
//   • Loop until stable so multi-anchor rows (MS7040 = MAX(MS7000, MS7015) + n)
//     pick up shifts in the right order.
//   • Skip rows whose actual_date is set (already done, never shift).
// =============================================================================
export type CascadeShift = {
  milestone_id:     string;
  milestone_code:   string | null;
  name:             string;
  old_planned_date: string | null;
  new_planned_date: string;
  sort_order:       number;
};

function computeShiftCascade(
  editedMilestoneId: string,
  newPlannedDate:    string,
  all:               ProjectMilestone[],
): CascadeShift[] {
  const sorted = [...all].sort((a, b) => a.sort_order - b.sort_order);
  const byId   = new Map(sorted.map(m => [m.milestone_id, m] as const));
  const byCode = new Map<string, ProjectMilestone>();
  for (const m of sorted) if (m.milestone_code) byCode.set(m.milestone_code, m);

  const newDates = new Map<string, string>();
  newDates.set(editedMilestoneId, newPlannedDate);

  function resolveAnchorDate(m: ProjectMilestone): string | null {
    return newDates.get(m.milestone_id) ?? m.planned_date ?? null;
  }

  let changed = true;
  let iterations = 0;
  while (changed && iterations < 50) {
    changed = false;
    iterations++;
    for (const m of sorted) {
      if (m.milestone_id === editedMilestoneId) continue;
      if (m.actual_date)                        continue;
      if (m.dependency_anchor === "standalone") continue;

      let anchorDate: string | null = null;
      if (m.dependency_anchor === "previous") {
        const prev = sorted.filter(x => x.sort_order < m.sort_order).slice(-1)[0];
        if (!prev) continue;
        anchorDate = resolveAnchorDate(prev);
      } else {
        const codes = m.dependency_anchor.split(",").map(c => c.trim()).filter(Boolean);
        const dates: string[] = [];
        for (const code of codes) {
          const anchor = byCode.get(code);
          if (anchor) {
            const d = resolveAnchorDate(anchor);
            if (d) dates.push(d);
          }
        }
        if (dates.length === 0) continue;
        anchorDate = dates.sort()[dates.length - 1];
      }
      if (!anchorDate) continue;

      const formulaDate = addWorkingDaysIso(anchorDate, m.offset_days ?? 0);
      const currentOverride = newDates.get(m.milestone_id);
      if (currentOverride !== formulaDate && formulaDate !== m.planned_date) {
        newDates.set(m.milestone_id, formulaDate);
        changed = true;
      }
    }
  }

  const result: CascadeShift[] = [];
  for (const [id, newDate] of newDates.entries()) {
    if (id === editedMilestoneId) continue;
    const m = byId.get(id);
    if (!m) continue;
    if (newDate === m.planned_date) continue;
    result.push({
      milestone_id:     m.milestone_id,
      milestone_code:   m.milestone_code,
      name:             m.name,
      old_planned_date: m.planned_date,
      new_planned_date: newDate,
      sort_order:       m.sort_order,
    });
  }
  return result.sort((a, b) => a.sort_order - b.sort_order);
}

export default function MilestonesTab({ project, canManage }: Props) {
  const projectId: string = project.project_id;

  const { data: milestones = [], refetch, isLoading } = useQuery({
    queryKey: ["project_formal_milestones", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_formal_milestones")
        .select("*")
        .eq("project_id", projectId)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as ProjectMilestone[];
    },
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["milestone_templates", project.client_id],
    enabled: canManage,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("milestone_templates")
        .select("template_id, template_name, description, is_default, client_id")
        .eq("is_active", true)
        .order("template_name");
      if (error) throw error;
      return (data ?? []) as MilestoneTemplate[];
    },
  });

  const [editing, setEditing] = useState<ProjectMilestone | "new" | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // T-202 Chunk C — when an edit shifts a milestone's planned_date, we
  // compute the cascade and stash it here. The ShiftCascadeModal renders
  // when this is non-null.
  const [pendingCascade, setPendingCascade] = useState<{
    editedMilestoneId: string;
    newPlannedDate:    string;
    shifts:            CascadeShift[];
  } | null>(null);

  const grouped = useMemo(() => {
    const active   = milestones.filter(m => m.status === "planned" || m.status === "in_progress" || m.status === "blocked");
    const done     = milestones.filter(m => m.status === "done");
    const deferred = milestones.filter(m => m.status === "deferred");
    return { active, done, deferred };
  }, [milestones]);

  const markDoneMut = useMutation({
    mutationFn: async (m: ProjectMilestone) => {
      const today = new Date().toISOString().slice(0, 10);
      const { error: e } = await supabase
        .from("project_formal_milestones")
        .update({ actual_date: today, status: "done" })
        .eq("milestone_id", m.milestone_id);
      if (e) throw e;
    },
    onSuccess: () => refetch(),
    onError: (e: any) => setError(`Couldn't mark done: ${e?.message ?? e}`),
  });

  const deleteMut = useMutation({
    mutationFn: async (m: ProjectMilestone) => {
      const { error: e } = await supabase
        .from("project_formal_milestones")
        .delete()
        .eq("milestone_id", m.milestone_id);
      if (e) throw e;
    },
    onSuccess: () => refetch(),
    onError: (e: any) => setError(`Couldn't delete: ${e?.message ?? e}`),
  });

  if (isLoading) {
    return <div className="py-10 flex justify-center"><Spinner /></div>;
  }

  const isEmpty = milestones.length === 0;

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5"/>
          <div className="flex-1">{error}</div>
          <button onClick={() => setError(null)} className="text-text-muted hover:text-text" type="button"><X size={14}/></button>
        </div>
      )}

      {/* Header strip */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-[18px] font-semibold tracking-tight inline-flex items-center gap-2">
            <Flag size={16} className="text-accent"/>
            Milestones
            {milestones.length > 0 && (
              <span className="text-text-muted text-[13px] font-normal">· {milestones.length}</span>
            )}
          </h2>
          <div className="text-[12px] text-text-muted mt-0.5">
            Client-reportable milestones for this project. Used in client reports + forecasting.
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="secondary" icon={Sparkles} size="sm" onClick={() => setApplyOpen(true)}>
              Apply template
            </Button>
            <Button icon={Plus} size="sm" onClick={() => setEditing("new")}>
              Add milestone
            </Button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {isEmpty && (
        <Card>
          <div className="p-6 text-center text-[13px] text-text-muted">
            <Flag size={28} className="mx-auto mb-2 text-text-dim"/>
            <div className="font-medium text-text mb-1">No milestones yet</div>
            <div>
              {canManage
                ? "Apply a template (NBN Tower Greenfield, etc.) to seed standard milestones, or add one manually."
                : "No milestones have been added to this project yet."}
            </div>
            {canManage && (
              <div className="mt-4 flex justify-center gap-2">
                <Button icon={Sparkles} variant="secondary" onClick={() => setApplyOpen(true)}>Apply template</Button>
                <Button icon={Plus} onClick={() => setEditing("new")}>Add manually</Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Active section */}
      {grouped.active.length > 0 && (
        <Section title="Active" count={grouped.active.length} milestones={grouped.active} allMilestones={milestones} canManage={canManage}
                 onEdit={setEditing} onMarkDone={(m) => markDoneMut.mutate(m)} onDelete={(m) => deleteMut.mutate(m)} />
      )}

      {/* Done section */}
      {grouped.done.length > 0 && (
        <Section title="Done" count={grouped.done.length} milestones={grouped.done} allMilestones={milestones} canManage={canManage}
                 onEdit={setEditing} onMarkDone={null} onDelete={(m) => deleteMut.mutate(m)} />
      )}

      {/* Deferred section */}
      {grouped.deferred.length > 0 && (
        <Section title="Deferred" count={grouped.deferred.length} milestones={grouped.deferred} allMilestones={milestones} canManage={canManage}
                 onEdit={setEditing} onMarkDone={(m) => markDoneMut.mutate(m)} onDelete={(m) => deleteMut.mutate(m)} />
      )}

      {/* Edit / Add modal */}
      {editing && (
        <MilestoneEditor
          projectId={projectId}
          existing={editing === "new" ? null : editing}
          existingMilestones={milestones}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            refetch();
            // If this was an edit (saved !== null) AND planned_date changed
            // AND a date is now set, compute cascade. Show the modal only if
            // the chain produces ≥1 downstream shift — otherwise nothing to
            // confirm.
            if (
              saved &&
              saved.new_planned_date &&
              saved.new_planned_date !== saved.old_planned_date
            ) {
              const shifts = computeShiftCascade(
                saved.milestone_id,
                saved.new_planned_date,
                milestones,
              );
              if (shifts.length > 0) {
                setPendingCascade({
                  editedMilestoneId: saved.milestone_id,
                  newPlannedDate:    saved.new_planned_date,
                  shifts,
                });
              }
            }
          }}
        />
      )}

      {/* Apply template modal */}
      {applyOpen && (
        <ApplyTemplateModal
          projectId={projectId}
          project={project}
          templates={templates}
          existingCount={milestones.length}
          onClose={() => setApplyOpen(false)}
          onApplied={() => { setApplyOpen(false); refetch(); }}
        />
      )}

      {/* Shift cascade modal — Chunk C */}
      {pendingCascade && (
        <ShiftCascadeModal
          projectId={projectId}
          shifts={pendingCascade.shifts}
          onClose={() => setPendingCascade(null)}
          onApplied={() => { setPendingCascade(null); refetch(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Section — list of milestones grouped by status
// -----------------------------------------------------------------------------
function Section({
  title, count, milestones, allMilestones, canManage, onEdit, onMarkDone, onDelete,
}: {
  title: string;
  count: number;
  milestones: ProjectMilestone[];
  allMilestones: ProjectMilestone[];   // pass-through for forecast-date compute
  canManage: boolean;
  onEdit: (m: ProjectMilestone) => void;
  onMarkDone: ((m: ProjectMilestone) => void) | null;
  onDelete: (m: ProjectMilestone) => void;
}) {
  return (
    <div>
      <div className="text-[11px] font-medium text-text-muted uppercase tracking-wider mb-2">
        {title} <span className="text-text-dim">· {count}</span>
      </div>
      <Card>
        <div className="divide-y divide-line/40">
          {milestones.map(m => (
            <MilestoneRow
              key={m.milestone_id}
              m={m}
              allMilestones={allMilestones}
              canManage={canManage}
              onEdit={() => onEdit(m)}
              onMarkDone={onMarkDone ? () => onMarkDone(m) : null}
              onDelete={() => {
                if (confirm(`Delete milestone "${m.name}"? This cannot be undone.`)) onDelete(m);
              }}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MilestoneRow — single row in the section list
// -----------------------------------------------------------------------------
function MilestoneRow({
  m, allMilestones, canManage, onEdit, onMarkDone, onDelete,
}: {
  m: ProjectMilestone;
  allMilestones: ProjectMilestone[];
  canManage: boolean;
  onEdit: () => void;
  onMarkDone: (() => void) | null;
  onDelete: () => void;
}) {
  // T-202 — when planned_date is null, attempt to derive a forecast from the
  // dependency chain so manager sees what the date WOULD be (not just "—").
  const forecastDate = m.planned_date ? null : computeForecastDate(m, allMilestones);
  return (
    <div className="p-3 flex items-start gap-3 hover:bg-ink-raised/40">
      <div className="flex-1 min-w-0">
        {/* T-202: code-first heading. The MS-code is what teams talk about
            ("what's your 5004 date") — make it prominent. The descriptive name
            sits secondary. Manual ad-hoc milestones without a code show the
            name as the heading instead. */}
        <div className="flex items-baseline gap-2.5 flex-wrap">
          {m.is_starred && <Star size={13} className="text-accent fill-accent flex-shrink-0 self-center"/>}
          {m.milestone_code ? (
            <>
              <span className="font-mono text-[16px] font-semibold text-accent tracking-tight">
                {m.milestone_code}
              </span>
              <span className="text-[13px] text-text-muted">{m.name}</span>
            </>
          ) : (
            <span className="text-[15px] font-semibold text-text">{m.name}</span>
          )}
          <Pill label={STATUS_LABELS[m.status]} tone={STATUS_TONES[m.status]} small />
          {m.is_payment_trigger && (
            <span className="inline-flex items-center gap-0.5 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-400/15 text-amber-200 font-mono font-semibold">
              <DollarSign size={10}/>{m.payment_pct != null ? `${m.payment_pct}%` : "pay"}
            </span>
          )}
          {!m.client_visible && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-ink-raised text-text-muted font-mono">internal</span>
          )}
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[12px] text-text-muted">
          {m.scope_owner && (
            <span><span className="text-text-dim">Owner:</span> <span className="text-text">{m.scope_owner}</span></span>
          )}
          <span className="inline-flex items-center gap-1">
            <CalendarDays size={11}/>
            <span className="text-text-dim">Planned:</span>
            {m.planned_date ? (
              <span className="text-text">{fmtDate(m.planned_date)}</span>
            ) : forecastDate ? (
              <>
                <span className="text-text-muted italic">{fmtDate(forecastDate)}</span>
                <span className="text-[10px] uppercase font-mono px-1 rounded bg-ink-raised text-text-muted">fcst</span>
              </>
            ) : (
              <span className="text-text-dim">—</span>
            )}
          </span>
          {m.actual_date && (
            <span className="inline-flex items-center gap-1 text-ok">
              <Check size={11}/>
              <span className="text-text-dim">Actual:</span>
              <span>{fmtDate(m.actual_date)}</span>
            </span>
          )}
          {m.dependency_anchor !== "previous" && m.dependency_anchor !== "standalone" && (
            <span className="font-mono text-[11px] text-text-dim" title="Dependency anchor">
              ⛓ {m.dependency_anchor}{m.offset_days != null ? ` +${m.offset_days}d` : ""}
            </span>
          )}
        </div>

        {m.comment && (
          <div className="text-[12px] text-text mt-1.5 italic">{m.comment}</div>
        )}
        {canManage && m.internal_notes && (
          <div className="text-[11px] text-text-muted mt-1">
            <span className="text-text-dim uppercase tracking-wider mr-1">Internal:</span>
            {m.internal_notes}
          </div>
        )}
      </div>

      {canManage && (
        <div className="flex gap-1 flex-shrink-0">
          {onMarkDone && m.status !== "done" && (
            <button
              onClick={onMarkDone}
              className="text-text-muted hover:text-ok p-1.5 rounded hover:bg-ink-raised"
              title="Mark done (sets actual date to today)"
              type="button"
            ><Check size={14}/></button>
          )}
          <button
            onClick={onEdit}
            className="text-text-muted hover:text-accent p-1.5 rounded hover:bg-ink-raised"
            title="Edit milestone"
            type="button"
          ><Edit3 size={14}/></button>
          <button
            onClick={onDelete}
            className="text-text-muted hover:text-lost p-1.5 rounded hover:bg-ink-raised"
            title="Delete milestone"
            type="button"
          ><Trash2 size={14}/></button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// MilestoneEditor — add / edit modal
// -----------------------------------------------------------------------------
function MilestoneEditor({
  projectId, existing, existingMilestones, onClose, onSaved,
}: {
  projectId: string;
  existing: ProjectMilestone | null;
  existingMilestones: ProjectMilestone[];
  onClose: () => void;
  // T-202 Chunk C — onSaved returns info about what changed so the parent
  // can decide whether to launch the shift cascade modal. saved===null means
  // a new milestone (no cascade); otherwise we pass the milestone_id and
  // old/new planned_dates so the parent can compare and trigger the cascade
  // when planned_date moved.
  onSaved: (saved: {
    milestone_id:     string;
    old_planned_date: string | null;
    new_planned_date: string | null;
  } | null) => void;
}) {
  const [draft, setDraft] = useState({
    milestone_code: existing?.milestone_code ?? "",
    name:           existing?.name ?? "",
    scope_owner:    existing?.scope_owner ?? "",
    planned_date:   existing?.planned_date ?? "",
    actual_date:    existing?.actual_date ?? "",
    status:         (existing?.status ?? "planned") as MilestoneStatus,
    is_starred:     existing?.is_starred ?? false,
    is_payment_trigger: existing?.is_payment_trigger ?? false,
    payment_pct:    existing?.payment_pct != null ? String(existing.payment_pct) : "",
    client_visible: existing?.client_visible ?? true,
    comment:        existing?.comment ?? "",
    internal_notes: existing?.internal_notes ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null);
    if (!draft.name.trim()) {
      setErr("Name is required.");
      return;
    }
    setSaving(true);

    // payment_pct only persists when is_payment_trigger is true.
    let parsedPct: number | null = null;
    if (draft.is_payment_trigger && draft.payment_pct.trim() !== "") {
      const n = Number(draft.payment_pct);
      if (isNaN(n) || n < 0 || n > 100) {
        setErr("Payment % must be a number between 0 and 100.");
        setSaving(false);
        return;
      }
      parsedPct = n;
    }

    const payload: any = {
      project_id: projectId,
      milestone_code: draft.milestone_code.trim() || null,
      name: draft.name.trim(),
      scope_owner: draft.scope_owner.trim() || null,
      planned_date: draft.planned_date || null,
      actual_date: draft.actual_date || null,
      status: draft.status,
      is_starred: draft.is_starred,
      is_payment_trigger: draft.is_payment_trigger,
      payment_pct: parsedPct,
      client_visible: draft.client_visible,
      comment: draft.comment.trim() || null,
      internal_notes: draft.internal_notes.trim() || null,
    };

    // If marking done and no actual_date, default to today
    if (draft.status === "done" && !payload.actual_date) {
      payload.actual_date = new Date().toISOString().slice(0, 10);
    }

    if (!existing) {
      // New milestone — manual ad-hoc, template_line_id stays null. sort_order
      // = (max existing) + 10 so it lands at the end.
      const maxSort = existingMilestones.reduce((acc, m) => Math.max(acc, m.sort_order), 0);
      payload.sort_order = maxSort + 10;
      payload.dependency_anchor = "standalone";
    }

    const { error: e } = existing
      ? await supabase.from("project_formal_milestones").update(payload).eq("milestone_id", existing.milestone_id)
      : await supabase.from("project_formal_milestones").insert(payload);
    setSaving(false);
    if (e) {
      setErr(e.message);
      return;
    }
    if (existing) {
      onSaved({
        milestone_id:     existing.milestone_id,
        old_planned_date: existing.planned_date,
        new_planned_date: payload.planned_date,
      });
    } else {
      onSaved(null);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-md w-full max-w-[600px] my-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="font-medium">{existing ? "Edit milestone" : "Add milestone"}</div>
          <button onClick={onClose} className="text-text-muted hover:text-text" type="button"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-3">
          {err && (
            <div className="text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2">{err}</div>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Milestone code" hint="Optional (e.g. MS5004 for NBN POW)">
              <Input
                value={draft.milestone_code}
                mono
                onChange={e => setDraft(d => ({ ...d, milestone_code: e.target.value.toUpperCase() }))}
                placeholder="MS5004"
              />
            </Field>
            <Field label="Scope owner" hint="Who's responsible">
              <Select value={draft.scope_owner} onChange={e => setDraft(d => ({ ...d, scope_owner: e.target.value }))}>
                <option value="">— pick —</option>
                <option value="Coretel">Coretel</option>
                <option value="Hardline">Hardline</option>
                <option value="Essential Energy">Essential Energy</option>
                <option value="NBN Co">NBN Co</option>
                <option value="Visionstream">Visionstream</option>
                <option value="Other">Other</option>
              </Select>
            </Field>
          </div>

          <Field label="Name *">
            <Input
              value={draft.name}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Site Integrated"
              autoFocus
            />
          </Field>

          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Planned date">
              <Input
                type="date"
                value={draft.planned_date}
                onChange={e => setDraft(d => ({ ...d, planned_date: e.target.value }))}
              />
            </Field>
            <Field label="Actual date" hint="When fired">
              <Input
                type="date"
                value={draft.actual_date}
                onChange={e => setDraft(d => ({ ...d, actual_date: e.target.value }))}
              />
            </Field>
            <Field label="Status">
              <Select value={draft.status} onChange={e => setDraft(d => ({ ...d, status: e.target.value as MilestoneStatus }))}>
                <option value="planned">Planned</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
                <option value="deferred">Deferred</option>
                <option value="blocked">Blocked</option>
              </Select>
            </Field>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-[13px] text-text">
              <input type="checkbox" checked={draft.is_starred}
                     onChange={e => setDraft(d => ({ ...d, is_starred: e.target.checked }))}/>
              <Star size={12} className="text-accent"/> Starred (★)
            </label>
            <label className="flex items-center gap-2 text-[13px] text-text">
              <input type="checkbox" checked={draft.is_payment_trigger}
                     onChange={e => setDraft(d => ({ ...d, is_payment_trigger: e.target.checked, payment_pct: e.target.checked ? d.payment_pct : "" }))}/>
              <DollarSign size={12} className="text-amber-300"/> Payment trigger
            </label>
            <label className="flex items-center gap-2 text-[13px] text-text">
              <input type="checkbox" checked={draft.client_visible}
                     onChange={e => setDraft(d => ({ ...d, client_visible: e.target.checked }))}/>
              Client visible
            </label>
          </div>

          {/* Payment % only visible when payment trigger is on */}
          {draft.is_payment_trigger && (
            <Field label="Payment %" hint="% of contract value triggered by this milestone (e.g. 20 for 20%)">
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={draft.payment_pct}
                onChange={e => setDraft(d => ({ ...d, payment_pct: e.target.value }))}
                placeholder="e.g. 20"
              />
            </Field>
          )}

          <Field label="Client comment" hint="Shows on the client report">
            <Textarea
              rows={2}
              value={draft.comment}
              onChange={e => setDraft(d => ({ ...d, comment: e.target.value }))}
            />
          </Field>

          <Field label="Internal notes" hint="Not shown on client report">
            <Textarea
              rows={2}
              value={draft.internal_notes}
              onChange={e => setDraft(d => ({ ...d, internal_notes: e.target.value }))}
            />
          </Field>
        </div>

        <div className="px-4 py-3 border-t border-line flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button icon={Save} onClick={save} disabled={saving}>
            {saving ? "Saving…" : existing ? "Save changes" : "Add milestone"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ApplyTemplateModal — pick a template + start date, calls the seed RPC
// -----------------------------------------------------------------------------
function ApplyTemplateModal({
  projectId, project, templates, existingCount, onClose, onApplied,
}: {
  projectId: string;
  project: any;
  templates: MilestoneTemplate[];
  existingCount: number;
  onClose: () => void;
  onApplied: () => void;
}) {
  const [templateId, setTemplateId] = useState<string>(
    templates.find(t => t.is_default)?.template_id ?? templates[0]?.template_id ?? ""
  );
  const [startDate, setStartDate] = useState<string>(
    project.start_date ?? project.planned_start_date ?? new Date().toISOString().slice(0, 10)
  );
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function apply() {
    setErr(null);
    if (!templateId) { setErr("Pick a template."); return; }
    if (!startDate)  { setErr("Pick a project start date."); return; }
    setApplying(true);
    const { data, error } = await supabase
      .schema("app")
      .rpc("seed_project_milestones_from_template", {
        p_project_id: projectId,
        p_template_id: templateId,
        p_project_start_date: startDate,
      });
    setApplying(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onApplied();
  }

  const selectedTemplate = templates.find(t => t.template_id === templateId);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-md w-full max-w-[500px] my-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-accent"/>
            <div className="font-medium">Apply milestone template</div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text" type="button"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-3">
          {err && (
            <div className="text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2">{err}</div>
          )}

          {existingCount > 0 && (
            <div className="text-[12px] text-amber-200 border border-amber-400/30 bg-amber-400/[0.05] rounded px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
              <div>
                <strong>Re-seed:</strong> this project already has {existingCount} milestone{existingCount === 1 ? "" : "s"}.
                Template-sourced ones will be replaced. Manual ad-hoc milestones (added via "Add milestone") are preserved.
              </div>
            </div>
          )}

          <Field label="Template *">
            <Select value={templateId} onChange={e => setTemplateId(e.target.value)}>
              <option value="">— pick a template —</option>
              {templates.map(t => (
                <option key={t.template_id} value={t.template_id}>
                  {t.template_name}{t.is_default ? " (default)" : ""}
                </option>
              ))}
            </Select>
            {selectedTemplate?.description && (
              <div className="text-[11px] text-text-muted mt-1">{selectedTemplate.description}</div>
            )}
          </Field>

          <Field label="Project start date *" hint="Mon-Sat working days from this date are used to compute planned dates. Sundays skipped.">
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}/>
          </Field>

          <div className="text-[11px] text-text-muted">
            Standalone milestones (e.g. pre-mob items where timing varies) are seeded with no date — fill in manually as confirmed.
          </div>
        </div>

        <div className="px-4 py-3 border-t border-line flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={applying}>Cancel</Button>
          <Button icon={Sparkles} onClick={apply} disabled={applying || !templateId}>
            {applying ? "Seeding…" : "Apply template"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ShiftCascadeModal — Chunk C
//
// Confirms a downstream date cascade after a manager edits a milestone's
// planned_date. Each row has a checkbox so the manager can opt out of any
// shift they don't want (e.g. a downstream date that was manually set for a
// reason that doesn't follow the dependency formula).
// -----------------------------------------------------------------------------
function ShiftCascadeModal({
  projectId, shifts, onClose, onApplied,
}: {
  projectId: string;
  shifts:    CascadeShift[];
  onClose:   () => void;
  onApplied: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(shifts.map(s => s.milestone_id)),
  );
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function apply() {
    setErr(null);
    const updates = shifts
      .filter(s => selected.has(s.milestone_id))
      .map(s => ({
        milestone_id:     s.milestone_id,
        new_planned_date: s.new_planned_date,
      }));
    if (updates.length === 0) {
      onClose();
      return;
    }
    setApplying(true);
    const { error: e } = await supabase.schema("app").rpc(
      "batch_update_milestone_dates",
      { p_project_id: projectId, p_updates: updates },
    );
    setApplying(false);
    if (e) {
      setErr(e.message);
      return;
    }
    onApplied();
  }

  const selectedCount = shifts.filter(s => selected.has(s.milestone_id)).length;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-md w-full max-w-[640px] my-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div>
            <div className="font-medium">Shift downstream milestones?</div>
            <div className="text-[12px] text-text-muted mt-0.5">
              {shifts.length} downstream milestone{shifts.length === 1 ? "" : "s"} would shift based on the dependency chain. Uncheck any you want to leave alone.
            </div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text" type="button"><X size={18}/></button>
        </div>

        <div className="p-4">
          {err && (
            <div className="text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2 mb-3">{err}</div>
          )}

          <div className="border border-line rounded overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="bg-ink-raised text-text-muted">
                <tr>
                  <th className="text-left px-3 py-2 w-8"></th>
                  <th className="text-left px-3 py-2 font-mono">Code</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Was</th>
                  <th className="text-left px-3 py-2">Becomes</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map(s => {
                  const isSelected = selected.has(s.milestone_id);
                  return (
                    <tr
                      key={s.milestone_id}
                      className={`border-t border-line cursor-pointer hover:bg-ink-raised/50 ${isSelected ? "" : "opacity-50"}`}
                      onClick={() => toggle(s.milestone_id)}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(s.milestone_id)}
                          onClick={e => e.stopPropagation()}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-accent">{s.milestone_code ?? "—"}</td>
                      <td className="px-3 py-2 text-text">{s.name}</td>
                      <td className="px-3 py-2 text-text-muted">
                        {s.old_planned_date ? fmtDate(s.old_planned_date) : "—"}
                      </td>
                      <td className="px-3 py-2 text-text">{fmtDate(s.new_planned_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-[11px] text-text-muted mt-3 leading-relaxed">
            Completed milestones (with an actual date) are never shifted. Standalone milestones don't follow the chain so aren't included here.
          </div>
        </div>

        <div className="px-4 py-3 border-t border-line flex items-center justify-between gap-2">
          <div className="text-[12px] text-text-muted">
            {selectedCount} of {shifts.length} selected
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={applying}>Skip</Button>
            <Button icon={CalendarDays} onClick={apply} disabled={applying || selectedCount === 0}>
              {applying ? "Shifting…" : `Shift ${selectedCount} milestone${selectedCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
